/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';

import { downloadUrl } from '@apis/common';
import { uploadDelegationForm } from '@apis/delegationApis';
import pdfIcon from '@assets/img/icons/pdf.svg';
import selectFiles from '@chenyuwang/select-files';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { Dialog } from '@components/Dialog';
import { Layout } from '@components/Layout';
import { ProgressBar } from '@components/ProgressBar';
import { SpinnerModal } from '@components/SpinnerModal';
import { Stepper } from '@components/Stepper';
import { Reference } from '@components/utilities/MembershipForm/Reference';
import { steps } from '@data/membershipForm';
import { useCompanyApplication } from '@hooks/mutation/useCompanyApplication';
import { useDelegation } from '@hooks/queries/useDelegation';
import { useMutation } from '@hooks/useMutation';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { useButtonStyles, useEnabledSteps } from '@pages/utilities/MembershipForm/helpers';
import { makeMuiStyles, useCommonStyles, usePalette } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { FormError, MessageError } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '通関委任状',
    templateFilename: '通関委任状.pdf',
    informationInputPrompt:
      'D2DはD2D D2DはD2D MEMBERSに代わり、通関手続きを代行いたします。通関業法に基づき、通関委任状の作成・ご提出が必要です。',
    delegationFormReference: () => (
      <>
        通関業法（第22条1項）
        <br />
        <br />
        <a
          href="https://elaws.e-gov.go.jp/document?lawid=342AC0000000122"
          target="_blank"
          rel="noreferrer"
        >
          通関業者は、政令で定めるところにより、通関業務（第七条に規定する関連業務を含む。以下この項及び第三項において同じ。）
          に関して帳簿を設け、その収入に関する事項を記載するとともに、その取扱いに係る通関業務に関する書類を一定期間保存しなければならない。
        </a>
        <br />
        <br />
        通関業法 施行令（第8条二項2号）
        <br />
        <br />
        <a
          href="https://elaws.e-gov.go.jp/document?lawid=342CO0000000237"
          target="_blank"
          rel="noreferrer"
        >
          通関業務に関し、依頼者から依頼を受けたことを証する書類
        </a>
      </>
    ),
    downloadPrompt: '通関委任状フォームを用意しました。内容を確認後ダウンロードしてください。',
    downloadButton: '通関委任状フォームをダウンロード',
    scanPrompt: '印刷後、御社情報をご記入・社判を押印の上、スキャンしてください。',
    scanButton: 'スキャンが完了しました。step3へ',
    uploadPrompt: 'スキャンした通関委任状をアップロードしてください。',
    uploadButton: '通関委任状ファイルを選択',
    loadingText: (progress: number) => `アップロード中...${Math.round(progress * 100)}%`,
    successText: 'アップロード済み',
    error: 'エラー',
    unknownError: '不明なエラーが発生しました。',
    goBack: 'TOPへ戻る',
    prev: '戻る',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  informationInputPrompt: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    padding: '40px 0',
  },
  transactionConditionPrompt: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    padding: '40px 0',
    whiteSpace: 'pre-wrap',
  },
  sectionContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '3rem 0',
  },
  radioGroupInput: {
    ...common.flexInline,
    flex: 1,
    justifyContent: 'space-around',
  },
  stepRoot: {
    ...common.flexInline,
    [theme.breakpoints.down('sm')]: {
      ...common.flexColumnCenter,
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
    },
  },
  steps: {
    ...common.middleText,
    ...common.flexColumn,
    color: theme.palette.grey['500'],
    backgroundColor: theme.palette.background.default,
    padding: '2rem 6rem',
    margin: '2.5rem 0',
    [theme.breakpoints.down('sm')]: {
      padding: '0.5rem',
    },
  },
  stepNumber: {
    ...common.flexCenter,
    ...common.normalText,
    width: 32,
    height: 32,
    borderRadius: '50%',
    color: theme.palette.grey['500'],
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.grey[200],
    borderStyle: 'solid',
    borderWidth: 1,
    '$stepFocused &': {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
    },
  },
  stepNumberText: {
    ...common.normalText,
    ...common.flexCenter,
    height: 32,
    whiteSpace: 'pre',
    color: theme.palette.grey[700],
    margin: '0 0.75rem',
    '$stepFocused &': {
      fontWeight: 500,
      color: theme.palette.grey[800],
    },
  },
  divider: {
    backgroundColor: theme.palette.grey[200],
    width: 1,
    height: 100,
    marginLeft: 16,
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  stepPrompt: {
    ...common.middleText,
    paddingTop: 8,
    color: theme.palette.grey['500'],
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
  },
  stepContent: {
    ...common.flexColumn,
    flex: 1,
    alignItems: 'center',
  },
  stepButton: {
    marginTop: '1.5rem',
    maxWidth: 300,
  },
  stepFocused: {},
  fileContainer: {
    ...common.flexInline,
    marginTop: '1rem',
    alignItems: 'center',
  },
  fileTitle: {
    ...common.middleText,
    width: 250,
    color: theme.palette.grey['800'],
    margin: '0 1rem',
  },
}));

interface StepProps {
  index: number;
  prompt: string;
  buttonText: string;
  onClick: () => void;
  disabled: boolean;
  current: boolean;
  showLine?: boolean;
  renderButton?: () => React.ReactNode;
}

const Step: React.FC<StepProps> = (props) => {
  const {
    index,
    prompt,
    buttonText,
    current,
    disabled,
    onClick,
    showLine = true,
    renderButton = () => (
      <Button
        text={buttonText}
        color="primary"
        className={classes.stepButton}
        disabled={disabled}
        onClick={onClick}
      />
    ),
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.stepRoot, current && classes.stepFocused)}>
      <div className={classes.flexColumn}>
        <div className={classes.flexInline}>
          <div className={classes.stepNumber}>{index + 1}</div>

          <div className={classes.stepNumberText}>step {index + 1}</div>
        </div>

        <div className={classes.divider} hidden={!showLine} />
      </div>

      <div className={classes.stepContent}>
        <div className={classes.stepPrompt}>{prompt}</div>

        {renderButton()}
      </div>
    </div>
  );
};

export const DelegationForm: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useButtonStyles(),
    ...useStyles(),
  };

  const palette = usePalette();
  const history = useHistory();
  const [fileName, setFileName] = useState('');

  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const queryClient = useQueryClient();

  const uploadChecklistTask = useMutation(uploadDelegationForm);
  const companyApplication = useCompanyApplication();
  const enabledSteps = useEnabledSteps();
  const delegation = useDelegation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<React.ReactNode>('');

  const renderProgressBar = () => (
    <ProgressBar
      loading={uploadChecklistTask.isLoading}
      error={uploadChecklistTask.isError}
      success={uploadChecklistTask.isSuccess}
      progress={progress}
      loadingText={strings.loadingText}
      successText={strings.successText}
      classes={{
        container: classes.stepButton,
      }}
      onReset={() => uploadChecklistTask.reset()}
    />
  );

  const renderFile = () => (
    <div className={classes.fileContainer}>
      <a href={`${delegation?.data?.file}`} target="_blank" rel="noreferrer">
        <img src={pdfIcon} alt="Delegation Form PDF Icon" height={44} width={44} />
      </a>
      <a href={`${delegation?.data?.file}`} target="_blank" rel="noreferrer">
        <div className={classes.fileTitle}>{fileName}</div>
      </a>

      <IconButton
        onClick={() => uploadChecklistTask.reset()}
        aria-label="close"
        style={{ padding: 0 }}
      >
        <CloseIcon />
      </IconButton>
    </div>
  );

  const stepProps: StepProps[] = [
    {
      index: 0,
      prompt: strings.downloadPrompt,
      buttonText: strings.downloadButton,
      onClick: async () => {
        setCurrentIndex(Math.max(1, currentIndex));
        await downloadUrl({
          url: '/delegation/template/',
          name: strings.templateFilename,
          type: 'application/pdf',
        });
      },
      disabled: false,
      current: currentIndex === 0,
    },
    {
      index: 1,
      prompt: strings.scanPrompt,
      buttonText: strings.scanButton,
      onClick: () => {
        setCurrentIndex(Math.max(2, currentIndex));
      },
      disabled: currentIndex !== 1,
      current: currentIndex === 1,
    },
    {
      index: 2,
      prompt: strings.uploadPrompt,
      buttonText: strings.uploadButton,
      onClick: async () => {
        const files = await selectFiles({
          accept: 'application/pdf',
          multiple: false,
        });

        if (files !== null && files.length > 0) {
          const file = files[0];
          setFileName(file.name);
          const form = new FormData();
          form.append('file', file, 'Form.pdf');
          try {
            await uploadChecklistTask.mutateAsync({
              file,
              onProgress: setProgress,
            });
            queryClient.invalidateQueries(['/delegation/']);
          } catch (e) {
            if (e instanceof FormError) {
              // eslint-disable-next-line no-alert
              setDialogOpen(true);
              setDialogMessage(e.getDisplayMessage());
            }
            throw e;
          }
        }
      },
      disabled: currentIndex !== 2,
      current: currentIndex === 2,
      showLine: false,
      renderButton: uploadChecklistTask.isIdle
        ? undefined
        : !uploadChecklistTask.isSuccess
        ? renderProgressBar
        : renderFile,
    },
  ];

  const renderModal = () => (
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
      <span style={{ color: palette.error.main }}>{strings.error}</span>
      <br />
      {dialogMessage}
    </Dialog>
  );

  const onSubmit = async () => {
    try {
      await companyApplication.mutateAsync();
      history.push('/utilities/MembershipForm/Complete');
    } catch (e) {
      if (e instanceof MessageError) {
        setDialogOpen(true);
        setDialogMessage(
          <>
            {e.message}
            <br />
            <br />
            <a href="/utilities/Dashboard">
              <Button color="primary" text={strings.goBack} />
            </a>
          </>,
        );
        return;
      }

      setDialogOpen(true);
      if (e instanceof Error) {
        setDialogMessage(`${strings.unknownError}\n${e.message}`);
      }
    }
  };

  return (
    <Layout title={strings.title}>
      {() => (
        <Container>
          {renderModal()}
          <SpinnerModal open={companyApplication.isLoading} />
          <Stepper steps={steps} value={steps[3].name} enabledSteps={enabledSteps} />
          <div className={classes.informationInputPrompt}>{strings.informationInputPrompt}</div>
          <Reference>{strings.delegationFormReference()}</Reference>

          <div className={classes.steps}>
            {stepProps.map((p, i) => (
              <Step {...p} key={i} />
            ))}
          </div>

          <div className={classes.buttons}>
            <Button
              text={strings.prev}
              color="primary"
              className={clsx(classes.button, classes.prev)}
              onClick={() => history.push('/utilities/MembershipForm/Checklist')}
            />
            <Button
              text={formStrings.next}
              color="primary"
              className={clsx(classes.button, classes.next)}
              disabled={!uploadChecklistTask.isSuccess}
              onClick={onSubmit}
            />
          </div>
        </Container>
      )}
    </Layout>
  );
};
